// src/features/quotation/hooks/useQuotation.js
import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchQuotations,
  fetchQuotationById,
  fetchQuotationStats,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  updateQuotationStatus,
  sendQuotation,
  convertToInvoice,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
} from "../../../app/slice/quotationSlice";

// ✅ Create memoized selectors
const selectQuotations = (state) => state.quotations;
const selectQuotationsList = (state) => state.quotations?.quotations || [];
const selectSelectedQuotation = (state) =>
  state.quotations?.selectedQuotation || null;
const selectQuotationsLoading = (state) => state.quotations?.loading || false;
const selectQuotationsError = (state) => state.quotations?.error || null;
const selectQuotationsSuccess = (state) => state.quotations?.success || null;
const selectQuotationsFilters = (state) =>
  state.quotations?.filters || {
    status: "all",
    search: "",
    dateRange: { start: null, end: null },
    clientId: "all",
    minAmount: null,
    maxAmount: null,
    tags: [],
  };
const selectQuotationsSortBy = (state) => state.quotations?.sortBy || "date";
const selectQuotationsSortOrder = (state) =>
  state.quotations?.sortOrder || "desc";
const selectQuotationsStats = (state) =>
  state.quotations?.stats || {
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
    converted: 0,
    totalValue: 0,
    averageValue: 0,
    conversionRate: 0,
    responseRate: 0,
  };

export const useQuotation = () => {
  const dispatch = useDispatch();

  // ✅ Use memoized selectors
  const quotations = useSelector(selectQuotationsList);
  const selectedQuotation = useSelector(selectSelectedQuotation);
  const loading = useSelector(selectQuotationsLoading);
  const error = useSelector(selectQuotationsError);
  const success = useSelector(selectQuotationsSuccess);
  const filters = useSelector(selectQuotationsFilters);
  const sortBy = useSelector(selectQuotationsSortBy);
  const sortOrder = useSelector(selectQuotationsSortOrder);
  const stats = useSelector(selectQuotationsStats);

  // Fetch all quotations
  const getQuotations = useCallback(
    (filterParams = {}) => {
      dispatch(fetchQuotations({ ...filters, ...filterParams }));
    },
    [dispatch, filters],
  );

  // Fetch quotation by ID
  const getQuotationById = useCallback(
    (id) => {
      dispatch(fetchQuotationById(id));
    },
    [dispatch],
  );

  // Fetch statistics
  const getStats = useCallback(() => {
    dispatch(fetchQuotationStats());
  }, [dispatch]);

  // Create new quotation
  const createNewQuotation = useCallback(
    async (data) => {
      const result = await dispatch(createQuotation(data));
      return result;
    },
    [dispatch],
  );

  // Update quotation
  const updateExistingQuotation = useCallback(
    async (id, data) => {
      const result = await dispatch(updateQuotation({ id, data }));
      return result;
    },
    [dispatch],
  );

  // Delete quotation
  const deleteExistingQuotation = useCallback(
    async (id) => {
      const result = await dispatch(deleteQuotation(id));
      return result;
    },
    [dispatch],
  );

  // Update status
  const updateStatus = useCallback(
    async (id, status) => {
      const result = await dispatch(updateQuotationStatus({ id, status }));
      return result;
    },
    [dispatch],
  );

  // Send quotation
  const sendQuotationToClient = useCallback(
    async (id) => {
      const result = await dispatch(sendQuotation(id));
      return result;
    },
    [dispatch],
  );

  // Convert to invoice
  const convertToInvoiceQuotation = useCallback(
    async (id) => {
      const result = await dispatch(convertToInvoice(id));
      return result;
    },
    [dispatch],
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  // Clear filters
  const clearAllFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  // Update sort
  const updateSort = useCallback(
    (newSortBy, newSortOrder) => {
      dispatch(setSort({ sortBy: newSortBy, sortOrder: newSortOrder }));
    },
    [dispatch],
  );

  // Clear messages
  const clearErrorMessage = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearSuccessMessage = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  // Auto-fetch on mount
  useEffect(() => {
    getQuotations();
    getStats();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    getQuotations();
  }, [filters, sortBy, sortOrder]);

  return {
    // State
    quotations,
    selectedQuotation,
    loading,
    error,
    success,
    filters,
    sortBy,
    sortOrder,
    stats,

    // Actions
    getQuotations,
    getQuotationById,
    getStats,
    createNewQuotation,
    updateExistingQuotation,
    deleteExistingQuotation,
    updateStatus,
    sendQuotationToClient,
    convertToInvoiceQuotation,
    updateFilters,
    clearAllFilters,
    updateSort,
    clearErrorMessage,
    clearSuccessMessage,
  };
};

export default useQuotation;
